import { formatDateTime } from '@pickleballinc/lib/format-dates';
import { getCDNURL } from '@pickleballinc/lib/get-cdn-url';
import { formatRoundText } from '@pickleballinc/ui/lib/formatRoundText';
import moment from 'moment';

import { publicEnv } from '@/lib/env';
import { isGameMakerModule } from '@/modules/players/v3/utils/play-opportunities';
import { TEAM_EVENTS } from '@/modules/results/constants';
import { ActiveEventFlat, Match, TournamentType } from '@/modules/results/types';
import {
  Court,
  OptionPod,
  Statistics,
  TeamLeagueType,
  TLMatchup,
} from '@/modules/results/types/teamLeaguesResults.types';
import { CardType } from '@/modules/results/types/types';
import { RankingStatusBadgeEnum } from '@/types/types';

export const isValidDate = (dateString: unknown): boolean => {
  if (typeof dateString === 'string' || typeof dateString === 'number') {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  } else {
    return false;
  }
};

export const isValidString = (str: string) => {
  return typeof str === 'string' && str.trim() !== '' && str !== 'Invalid date';
};

export function formatValueToNDecimals(value: number | null, noOfDecimals: number) {
  if (!value) return 0;
  if (noOfDecimals % 1 !== 0 || noOfDecimals < 0) {
    return value;
  }

  if (value % 1 === 0) {
    return value;
  } else {
    return parseFloat(value.toFixed(noOfDecimals));
  }
}

export function formatTournamentCard(card: TournamentType): CardType {
  return {
    id: card.TournamentID,
    logo: card.Logo,
    title: card.Title,
    locationCity: card.LocationCity,
    locationState: card.LocationState,
    locationCountry: card.LocationCountry,
    fromDate: formatDateTime(card.TourneyFromDate),
    toDate: formatDateTime(card.TourneyToDate),
    slug: card.slug,
    type: 'tournaments',
    rankingBadgeTitle: card.badge_title ?? RankingStatusBadgeEnum.DEFAULT,
    showRankingBadge: card.show_badge_title,
  };
}

export function formatTeamLeagueCard(card: TeamLeagueType): CardType {
  return {
    id: `${card.uuid}-${card.matchupGroupUuid}`,
    logo: card.logo?.url,
    title: card.title,
    organizationID: card.organizationUuid,
    locationCity: card.location?.city,
    locationState: card.location?.stateInfo?.abbreviation,
    locationCountry: card.location?.countryInfo?.abbreviation,
    fromDate: formatDateTime(card.startDate),
    toDate: formatDateTime(card.endDate),
    slug: card.slug,
    divisions: card.divisions,
    type: 'team-leagues',
  };
}

export function getProfileImageUrl(profileImage: string | undefined) {
  return getCDNURL(
    profileImage || `${publicEnv.NEXT_PUBLIC_IMAGE_CDN_URL}/global/legacy/profile-image.svg`,
    64,
    64
  );
}

export const sortPodOptions = (pods: OptionPod[]): OptionPod[] => {
  const extractNumber = (label: string) => {
    const match = label.match(/\d+$/);
    return match ? parseInt(match[0], 10) : Infinity;
  };

  return pods.sort((a, b) => extractNumber(a.label) - extractNumber(b.label));
};

export const calculateTotalMatchups = (team1Wins: string, team2Wins: string) => {
  const sumMachups = parseInt(team1Wins) + parseInt(team2Wins);
  return sumMachups;
};

export const calculateWinsPercentage = (firstArgWins: number, secondArgWins: number): number => {
  const sum = firstArgWins + secondArgWins;
  if (sum === 0) return sum;

  const result = (firstArgWins / sum) * 100;
  return parseFloat(result.toFixed(2));
};

export function calculateWLPercentage(wins: number, losses: number) {
  if (wins + losses === 0) {
    return 'No played matches';
  }
  const sum = wins + losses;
  const result = (wins / sum) * 100;
  return parseFloat(result.toFixed(2));
}

export function formatNameWithInitial(name: string) {
  if (typeof name !== 'string' || name.trim() === '') {
    return '';
  }
  const words = name.trim().split(' ');

  if (words.length > 1) {
    const firstWord = words[0];
    return `${firstWord?.charAt(0)}. ${words[words.length - 1]}`;
  }
  return name;
}

export function formatFirstInitialLastName(fname: string, lname: string, suffix: string) {
  if (typeof fname !== 'string' || typeof lname !== 'string') {
    return '';
  }

  if (fname.trim() === '' || lname.trim() === '') {
    return '';
  }

  return `${fname.charAt(0).toUpperCase()}. ${lname.charAt(0).toUpperCase()}${lname.slice(1).toLowerCase()} ${suffix}`;
}

export function validConcateCourtTitles(courts: Court[]) {
  const validTitles = courts.filter((court) => court.title && court.title.trim() !== '');
  if (!validTitles || validTitles.length === 0) return null;

  return validTitles.map((court) => court.title).join(', ');
}

export const calculateDuration = (
  start: string,
  end: string,
  utc: moment.DurationInputArg1,
  abbreviation: string
) => {
  const startTime = new Date(start).getTime();
  const endTime = new Date(end).getTime();
  let formattedDuration = '0';
  let formattedStartTime = '0';
  let formattedEndTime = '0';

  if (start && end) {
    const duration = (endTime - startTime) / 1000; // Duration in seconds
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    formattedDuration = `${minutes} min ${seconds} sec`;
  }

  if (start) {
    formattedStartTime = formatDateTime(new Date(start).toISOString(), {
      utcOffset: Number(utc),
      timezoneAbbreviation: abbreviation,
      type: 'time',
    });
  }
  if (end) {
    formattedEndTime = formatDateTime(new Date(end).toISOString(), {
      utcOffset: Number(utc),
      timezoneAbbreviation: abbreviation,
      type: 'time',
    });
  }

  return {
    formattedDuration,
    formattedStartTime,
    formattedEndTime,
  };
};

export const getStatistics = (matchupData: TLMatchup) => {
  const utc = matchupData?.timezone.utcOffset;
  const abbreviation = matchupData?.timezone.abbreviation;

  const statistics: Statistics = {
    duration: calculateDuration(
      matchupData.plannedStartDate,
      matchupData.plannedEndDate,
      utc,
      abbreviation
    ),
    teamOnePointsEarned: matchupData.teamOnePointsEarned,
    teamTwoPointsEarned: matchupData.teamTwoPointsEarned,
    teamOnePointsDiff: matchupData.teamOnePointsDiffPct,
    teamTwoPointsDiff: matchupData.teamTwoPointsDiffPct,
  };

  return statistics;
};

export const addTeamEventsToEventsList = (
  hasTeamEvents: boolean | undefined,
  activeEventsData: ActiveEventFlat[]
) => {
  const activeEventDataCopy: ActiveEventFlat[] = [...activeEventsData];
  if (hasTeamEvents) {
    const firstEvent = activeEventDataCopy[0];
    let hasPro = false;

    if (firstEvent) {
      hasPro = firstEvent.group_title === 'Pro Events';
    }
    if (hasPro) {
      // Insert after Pro events
      activeEventDataCopy.splice(1, 0, TEAM_EVENTS);
    } else {
      // No Pro events - insert at the start
      activeEventDataCopy.unshift(TEAM_EVENTS);
    }
  }
  return activeEventDataCopy;
};

export const getMatchTypeText = (roundText: string, roundNumber: number, inBracketType: string) => {
  let matchTypeText = '';
  if (roundText && roundText.trim() !== '') {
    matchTypeText = formatRoundText({ roundText: roundText, showGold: true });
  } else if (roundNumber) {
    matchTypeText = `Round: ${roundNumber}`;
    if (inBracketType === 'L1') {
      matchTypeText = `Backdraw Round: ${roundNumber}`;
    } else if (inBracketType === 'RR') {
      matchTypeText = `Round Robin Round: ${roundNumber}`;
    }
  }

  return matchTypeText;
};

export function isGameMakerMatch(match?: Pick<Match, 'module_for'>): boolean {
  return match?.module_for !== undefined && isGameMakerModule(match.module_for);
}

export function isPrivateMatch(match?: Pick<Match, 'set_private'>): boolean {
  return match?.set_private === true;
}

export function isTeamEventByUuids(
  teamOneUuid?: string | null,
  teamLeagueTeamOneUuid?: string | null,
  teamTwoUuid?: string | null,
  teamLeagueTeamTwoUuid?: string | null
): boolean {
  return !!((teamOneUuid && !teamLeagueTeamOneUuid) || (teamTwoUuid && !teamLeagueTeamTwoUuid));
}
